import React, { useState } from 'react';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { Button, Display } from 'Views/Components/Button/Button';
import FilterContainer from 'Views/Components/FilterContainer/FilterContainer';
import { DisplayType } from 'Views/Components/Models/Enums';
import { DatePicker } from 'Views/Components/DatePicker/DatePicker';
import { useLocalStore } from 'mobx-react';
import useMountedQuery from 'Hooks/useMountedQuery';
import { getStudyPlansFilters } from 'Queries/GraphQlQueries/StudyPlan/StudyPlanQueries';
import StatusChip from 'Views/Components/Chip/ProgramVersionStatusChip';
import { programStatus, programStatusOptions } from 'Models/Enums';
import { action } from 'mobx';
import { Select, SelectOption } from 'Views/Components/Select/Select';
import { sortByUniqueAscending } from 'Views/Components/StudyPlanCollection/StudyPlanCollectionHelpers';
import { capitalize, toLower } from 'lodash';

export interface IStudyPlanFilterState {
	lastModifiedAfter?: Date,
	lastModifiedBefore?: Date,
	programCode?: string,
	programTitle?: string,
	commencingYear?: string,
	versionStatus?: string,
	createdBy?: string,
	modifiedBy?: string,
}

interface StudyPlanCollectionSearchFilterProps {
	userId: string;
	filterState: IStudyPlanFilterState;
	onSearch: (searchValue: string) => void;
}

const versionStatusOptions: SelectOption<programStatus>[] = [
	{
		display: <StatusChip status={programStatusOptions.PUBLISHED} />,
		value: 'PUBLISHED',
	},
	{
		display: <StatusChip status={programStatusOptions.APPROVED} />,
		value: 'APPROVED',
	},
	{
		display: <StatusChip status={programStatusOptions.IN_REVIEW} />,
		value: 'IN_REVIEW',
	},
	{
		display: <StatusChip status={programStatusOptions.DRAFT} />,
		value: 'DRAFT',
	},
	{
		display: <StatusChip status={programStatusOptions.ARCHIVED} />,
		value: 'ARCHIVED',
	},
];

const StudyPlanCollectionSearchFilter = (props: StudyPlanCollectionSearchFilterProps) => {
	const {
		userId,
		filterState,
		onSearch,
	} = props;

	const [showFilters, setFilterVisibility] = useState(false);
	const searchFieldStore = useLocalStore(() => ({ value: undefined }));
	const [programCodeOptions, setProgramCodeOptions] = useState<SelectOption<string>[]>([]);
	const [programTitleOptions, setProgramTitleOptions] = useState<SelectOption<string>[]>([]);
	const [createdByOptions, setCreatedByOptions] = useState<SelectOption<string>[]>([]);
	const [modifiedByOptions, setModifiedByOptions] = useState<SelectOption<string>[]>([]);
	const [commencingYearOptions, setCommencingYearOptions] = useState<SelectOption<string>[]>([]);

	useMountedQuery(
		() => getStudyPlansFilters(userId),
		[userId],
		(res, isMounted) => {
			if (!isMounted) {
				return;
			}

			const _programCodeOptions = sortByUniqueAscending(
				res.data.studyPlanEntitys.map(plan => ({
					display: plan.programVersion.program.code,
					value: plan.programVersion.program.code,
				})),
			);

			const _programTitleOptions = sortByUniqueAscending(
				res.data.studyPlanEntitys.map(plan => ({
					display: plan.programVersion.program.title,
					value: plan.programVersion.program.title,
				})),
			);

			const yearOptions = sortByUniqueAscending(
				res.data.studyPlanEntitys
					.map(plan => ({
						display: plan.programVersion.commencingYear.toString(),
						value: plan.programVersion.commencingYear.toString(),
					})),
			).reverse();

			const _createdByOptions = sortByUniqueAscending(
				res.data.studyPlanEntitys.map(plan => ({
					display: plan.ownerName,
					value: plan.owner,
				})),
			);

			const _modifiedByOptions = sortByUniqueAscending(
				res.data.studyPlanEntitys.map(plan => ({
					display: plan.modifiedByUserName,
					value: plan.modifiedBy,
				})),
			);

			setProgramCodeOptions(_programCodeOptions);
			setProgramTitleOptions(_programTitleOptions);
			setCommencingYearOptions(yearOptions);
			setCreatedByOptions(_createdByOptions);
			setModifiedByOptions(_modifiedByOptions);
		},
	);

	const resetFilters = action(() => {
		filterState.programCode = undefined;
		filterState.programTitle = undefined;
		filterState.commencingYear = undefined;
		filterState.versionStatus = undefined;
		filterState.lastModifiedAfter = undefined;
		filterState.lastModifiedBefore = undefined;
		filterState.createdBy = undefined;
		filterState.modifiedBy = undefined;
	});

	return (
		<>
			<div className="search-input-wrapper">
				<div className="search-bar">
					<TextField
						id="search-input"
						className="search-input"
						placeholder={`Search ${toLower(window.lexicon.studyPlans)}`}
						model={searchFieldStore}
						modelProperty="value"
						onAfterChange={evt => onSearch(evt.target.value)}
					/>
				</div>
				<div className="search-actions">
					<Button
						display={showFilters ? Display.Outline : Display.Solid}
						icon={{ icon: 'filter-list', iconPos: 'icon-right' }}
						onClick={() => setFilterVisibility(prev => !prev)}
						testId="study-plan-collection-search-filter-btn"
					>
						Filter
					</Button>
				</div>
			</div>
			<div className="filters">
				<FilterContainer
					showFilters={showFilters}
					onClear={resetFilters}
				>
					<Select
						labelVisible={false}
						label={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.code)}`}
						placeholder={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.code)}`}
						model={filterState}
						modelProperty="programCode"
						options={programCodeOptions}
						displayType={DisplayType.INLINE}
						inputProps={{
							'aria-label': `${capitalize(window.lexicon.program)} ${toLower(window.lexicon.code)}`,
						}}
					/>
					<Select
						labelVisible={false}
						label={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.title)}`}
						placeholder={`${capitalize(window.lexicon.program)} ${toLower(window.lexicon.title)}`}
						model={filterState}
						modelProperty="programTitle"
						options={programTitleOptions}
						displayType={DisplayType.INLINE}
						inputProps={{
							'aria-label': `${capitalize(window.lexicon.program)} ${toLower(window.lexicon.title)}`,
						}}
					/>
					<Select
						labelVisible={false}
						label={capitalize(window.lexicon.commencingYear)}
						placeholder={capitalize(window.lexicon.commencingYear)}
						model={filterState}
						modelProperty="commencingYear"
						options={commencingYearOptions}
						displayType={DisplayType.INLINE}
						inputProps={{
							'aria-label': capitalize(window.lexicon.commencingYear),
						}}
					/>
					<Select
						labelVisible={false}
						label="Version status"
						placeholder="Version status"
						model={filterState}
						modelProperty="versionStatus"
						options={versionStatusOptions}
						displayType={DisplayType.INLINE}
						inputProps={{
							'aria-label': 'Version status',
						}}
					/>
					<DatePicker
						id="last-modified-after"
						model={filterState}
						modelProperty="lastModifiedAfter"
						name="ModifiedAfter"
						label="Last modified after"
						placeholder="Last modified after"
						labelVisible={false}
						displayType={DisplayType.INLINE}
						clickToClear
					/>
					<DatePicker
						id="last-modified-before"
						model={filterState}
						modelProperty="lastModifiedBefore"
						name="ModifiedBefore"
						label="Last modified before"
						placeholder="Last modified before"
						labelVisible={false}
						displayType={DisplayType.INLINE}
						clickToClear
					/>
					<Select
						labelVisible={false}
						label="Last modified by"
						placeholder="Last modified by"
						model={filterState}
						modelProperty="modifiedBy"
						options={modifiedByOptions}
						displayType={DisplayType.INLINE}
						inputProps={{
							'aria-label': 'Last modified by',
						}}
					/>
					<Select
						labelVisible={false}
						label="Created by"
						placeholder="Created by"
						model={filterState}
						modelProperty="createdBy"
						options={createdByOptions}
						displayType={DisplayType.INLINE}
						inputProps={{
							'aria-label': 'Created by',
						}}
					/>
				</FilterContainer>
			</div>
		</>
	);
};

export default StudyPlanCollectionSearchFilter;
